<template>
  <div class="flex flex-col gap-3">
    <!-- Search -->
    <div class="relative">
      <font-awesome-icon
        :icon="['fas', 'magnifying-glass']"
        class="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500 text-base"
      />
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        placeholder="Start typing a name or address to search…"
        class="w-full pl-10 pr-3 py-2.5 border-2 border-blue-200 rounded-lg text-sm shadow-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
      />
    </div>

    <!-- Address book (internal staff + saved contacts, unified) -->
    <div>
      <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Address Book</p>
      <div v-if="loadingContacts" class="text-sm text-gray-400 py-2">Loading…</div>
      <template v-else-if="hasContacts">
        <section v-if="savedContacts.length">
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Saved Contacts</p>
          <ul class="divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden">
            <li v-for="entry in savedContacts" :key="entry.direct_address">
              <button
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                @click="selectRecipient(entry)"
              >
                <div class="flex items-center gap-2">
                  <span class="font-medium text-gray-900">{{ displayName(entry) }}</span>
                  <span v-if="entry.organization" class="text-gray-500 text-xs">({{ entry.organization }})</span>
                </div>
                <span v-if="participantDisplayName(entry)" class="text-gray-400 text-xs">{{ entry.direct_address }}</span>
              </button>
            </li>
          </ul>
          <p v-if="hasMoreSavedContacts" class="text-xs text-gray-500 mt-1.5">
            Showing the first {{ savedContactsLimit }} saved-contact matches — keep typing above to narrow results.
          </p>
        </section>

        <section v-if="internalContacts.length" :class="savedContacts.length ? 'mt-3' : ''">
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Internal Users</p>
          <ul class="divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden">
            <li v-for="entry in internalContacts" :key="entry.direct_address">
              <button
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                @click="selectRecipient(entry)"
              >
                <div class="flex items-center gap-2">
                  <span class="font-medium text-gray-900">{{ displayName(entry) }}</span>
                  <span class="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-200 rounded px-1">Internal</span>
                  <span v-if="entry.organization" class="text-gray-500 text-xs">({{ entry.organization }})</span>
                </div>
                <span v-if="participantDisplayName(entry)" class="text-gray-400 text-xs">{{ entry.direct_address }}</span>
              </button>
            </li>
          </ul>
          <p v-if="hasMoreInternalContacts" class="text-xs text-gray-500 mt-1.5">
            Showing the first {{ internalContactsLimit }} team matches — keep typing above to narrow results.
          </p>
        </section>
      </template>
      <p v-else-if="!contactsUnavailable" class="text-sm text-gray-400 py-1">No matches in your address book.</p>

      <!-- Global provider directory search -->
      <div class="mt-2">
        <button
          type="button"
          class="btn-link text-sm"
          @click="showDirectorySearch = true"
        >
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="mr-1" />
          Can't find them? Search provider directory
        </button>
      </div>
    </div>

    <ProviderDirectorySearchModal
      v-model="showDirectorySearch"
      selectable
      @select="selectFromDirectory"
      @address-book-updated="loadContacts"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import * as messagingApi from '@/services/api/providerMessaging.js';
import ProviderDirectorySearchModal from '@/components/shared/ProviderDirectorySearchModal.vue';
import { participantDisplayName } from '@/utils/providerMessaging.js';

// Address books grow unbounded server-side (contacts are append-only, staff directories
// aren't paginated). Keep each rendered section bounded, while giving the internal
// directory its own section and space in the initial view.
const INTERNAL_CONTACTS_LIMIT = 25;
const SAVED_CONTACTS_LIMIT = 20;

export default {
  name: 'RecipientPicker',
  components: { FontAwesomeIcon, ProviderDirectorySearchModal },

  emits: ['select'],

  data() {
    return {
      contacts: [],
      searchQuery: '',
      loadingContacts: true,
      contactsUnavailable: false,
      showDirectorySearch: false,
      internalContactsLimit: INTERNAL_CONTACTS_LIMIT,
      savedContactsLimit: SAVED_CONTACTS_LIMIT,
    };
  },

  computed: {
    filteredContacts() {
      const q = this.searchQuery.toLowerCase();
      return this.contacts.filter(
        (c) =>
          !q ||
          this.displayName(c).toLowerCase().includes(q) ||
          (c.organization || '').toLowerCase().includes(q) ||
          (c.direct_address || '').toLowerCase().includes(q),
      );
    },

    internalContacts() {
      return this.matchingInternalContacts.slice(0, INTERNAL_CONTACTS_LIMIT);
    },

    savedContacts() {
      return this.matchingSavedContacts.slice(0, SAVED_CONTACTS_LIMIT);
    },

    matchingInternalContacts() {
      return this.filteredContacts.filter((contact) => contact.source === 'internal');
    },

    matchingSavedContacts() {
      return this.filteredContacts.filter((contact) => contact.source === 'contact');
    },

    hasMoreInternalContacts() {
      return this.matchingInternalContacts.length > INTERNAL_CONTACTS_LIMIT;
    },

    hasMoreSavedContacts() {
      return this.matchingSavedContacts.length > SAVED_CONTACTS_LIMIT;
    },

    hasContacts() {
      return this.internalContacts.length || this.savedContacts.length;
    },

  },

  async created() {
    await this.loadContacts();
  },

  mounted() {
    // Native autofocus is unreliable when this component is inserted dynamically
    // (v-if inside a teleported modal) — focus explicitly instead.
    this.$refs.searchInput?.focus();
  },

  methods: {
    async loadContacts() {
      this.loadingContacts = true;
      try {
        this.contacts = await messagingApi.listContacts();
        this.contactsUnavailable = false;
      } catch {
        this.contactsUnavailable = true;
        this.contacts = [];
      } finally {
        this.loadingContacts = false;
      }
    },

    participantDisplayName,

    displayName(entry) {
      return participantDisplayName(entry) || entry.direct_address || 'Unknown';
    },

    selectRecipient(entry) {
      this.$emit('select', {
        direct_address: entry.direct_address,
        display_name: participantDisplayName(entry),
      });
    },

    async selectFromDirectory(result) {
      this.showDirectorySearch = false;
      // Save to address book (upsert — safe to re-call) so future compose sessions
      // find this provider without a directory search.
      try {
        await messagingApi.createContact({
          direct_address: result.direct_address,
          first_name: result.first_name ?? '',
          last_name: result.last_name ?? '',
          organization: result.organization ?? '',
          npi: result.npi ?? '',
          phone: result.phone ?? '',
          fax: result.fax ?? '',
          address1: result.address1 ?? '',
          address2: result.address2 ?? '',
          city: result.city ?? '',
          state: result.state ?? '',
          postal: result.postal ?? '',
        });
        await this.loadContacts();
        this.$root.$emit('show-success-toast', `Added ${this.displayName(result)} to your address book`);
      } catch (e) {
        console.warn('[RecipientPicker] failed to save directory contact:', e?.message);
      }
      this.$emit('select', {
        direct_address: result.direct_address,
        display_name: participantDisplayName(result),
      });
    },
  },
};
</script>
